import React, { FunctionComponent, useEffect, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Box, Button, Divider, Stack } from "@mui/material";

import { COLORS, Label14Medium,Label16SemiBold, Loading, SettingPrimaryLabel } from "components";

import { useGetConfigParameter, useSites } from "hooks";

import { getUserRole, USER_ROLES } from "recoils";
import { CSVLink } from "react-csv";
import { EsgExcelExport } from "assets";

import { getUsers, UserDTO } from "api";
import { EsgMultiselectControlledField } from "components/inputs/esg-multi-select-field";

import { User } from "./user";
import { InviteUsersForm } from "./invite-users-form";

interface UsersControllerProps {
  shouldFetchUsers: boolean;
}

const userRole = [
  {
    "label": USER_ROLES.AUDITOR,
    "name": "Auditor",
    "value": 1
  },
  {
    "label": USER_ROLES.DATA_PROVIDER,
    "name": "Data Provider",
    "value": 2
  },
  {
    "label": USER_ROLES.DATA_VALIDATOR,
    "name": "Data Validator",
    "value": 3
  },
  {
    "label": USER_ROLES.MODULE_ADMIN,
    "name": "Module Admin",
    "value": 4
  },
]

const userRoleApprovalFlow = [
  {
    "label": USER_ROLES.DATA_REVIEWER,
    "name": "Data Reviewer",
    "value": 5
  },
];


export const InvitedUsersList: FunctionComponent<UsersControllerProps> = () => {
  const { mutateAsync: fetchUser, data: users, isLoading: areUsersLoading } = useMutation(getUsers);

  const [dataEntriesStatus, setDataEntriesStatus] = useState<any>([]);
  const [selectedRole, setSelectedRole] = useState<any>([]);

  useEffect(() => {
    const roles = dataEntriesStatus.map((t: any) => t?.label?.toLowerCase());
    setSelectedRole(roles)
  }, [dataEntriesStatus])

  useEffect(() => {
    fetchUser(selectedRole?.join(", "))
  }, [selectedRole, fetchUser])

  const { data: sites } = useSites();
  const { data: configData } = useGetConfigParameter();

  const secondApproval = configData?.find((item: any) => item?.name === "second_layer_approval_feature")?.configValue;

  if (areUsersLoading) {
    return <Loading />;
  }

  // Export User
  const getSiteNameById = (ids: number[]) => {
    const siteMap = sites!?.reduce((acc: any, site: any) => {
      acc[site?.id] = site?.name;
      return acc;
    }, {});

    return ids?.map(id => siteMap[id])?.filter(Boolean);
  }

  const CsvEmissionSummary: any = users && users?.map((item: any) => ({
    "First Name": item?.firstName,
    "Last Name": item?.lastName,
    "User Email": item?.email,
    "Is User Active": item?.enabled === true ? "Yes" : "No",
    "User Role": getUserRole(item?.roles),
    "Data Provider Site": getSiteNameById(item?.sites),
  }));

  const activeUser: UserDTO[] = users?.filter((user) => user.enabled) ?? [];

  return (
    <>
      <InviteUsersForm />
      <Divider sx={{ width: '100%' }} color="#9C9C9C" />
      <Box sx={{ display: "flex", justifyContent: "space-between", px: "24px" }}>

        <Stack alignItems="center" direction="row" spacing={1}>
          {userRole && <Box sx={{ minWidth: "240px", width: "fit-content" }}>
            <EsgMultiselectControlledField
              label="View by Title"
              selectedValues={dataEntriesStatus}
              setSelectedValues={setDataEntriesStatus}
              options={secondApproval === "false" ? userRole : [...userRole, ...userRoleApprovalFlow].sort((a, b) => a.label.localeCompare(b.label))}
              isAllSelectedOnEmptySelection
            />
          </Box>}
        </Stack>
        {users && <CSVLink
          style={{ textDecoration: "none" }}
          data={CsvEmissionSummary}
          filename="User-List-Data.csv"
        >
          <Button
            sx={{ height: "36px", marginLeft: "3px", borderRadius: "8px", fontSize: "14px", fontFamily: "Inter", mt: "18px", width: "196px" }}
            type="submit"
            startIcon={<EsgExcelExport />}
            variant="contained"
          >
            <Label14Medium>Export User List</Label14Medium>
          </Button>
        </CSVLink>}
      </Box>
      <Divider sx={{ width: '100%' }} />
      <Box
        sx={{
          borderBottom: `1px solid ${COLORS.extraLightGray}`,
          flex: 2,
          overflowY: "auto",
          paddingBottom: 3,
          px: "24px"
        }}
      >
        <Box display="flex" alignItems="center" width="100%" marginTop="1" marginBottom="20px">
          <Box width="6%" my="8px">
            <SettingPrimaryLabel sx={{ color: COLORS.Romance, fontWeight: 500 }} />
          </Box>
          <Box width="16%" >
            <SettingPrimaryLabel sx={{ color: COLORS.Romance, fontWeight: 500 }}>
              Name
            </SettingPrimaryLabel>
          </Box>

          <Box width="30%" >
            <SettingPrimaryLabel sx={{ color: COLORS.Romance, fontWeight: 500 }}>
              Email
            </SettingPrimaryLabel>
          </Box>

          <Box width="22%" >
            <SettingPrimaryLabel sx={{ color: COLORS.Romance, fontWeight: 500 }}>
              Title
            </SettingPrimaryLabel>
          </Box>

          <Box width="20%" >
            <SettingPrimaryLabel sx={{ color: COLORS.Romance, fontWeight: 500 }}>
              Sites
            </SettingPrimaryLabel>
          </Box>
          <Box width="1%" >
            <SettingPrimaryLabel sx={{ color: COLORS.Romance, fontWeight: 500 }} />
          </Box>
        </Box>


        <Box>
          {activeUser?.length === 0 ? (
            <Box sx={{my: 4, display: "flex", justifyContent: "center"}} width="100%">
              <Label16SemiBold sx={{fontStyle: "italic"}} mt={1}>There are no users with the selected role.</Label16SemiBold>
            </Box>
          ) : (
            activeUser.map((user: UserDTO) => (
              <User key={user.id} userData={user} />
            ))
          )}
        </Box>
      </Box>
    </>
  );
};
